import { Switch, Redirect, Route } from 'react-router-dom';
import { lazy } from 'react';
import PrivateRoute from 'components/common/private-route';
import { PRICE_LIST_PERMISSION } from 'constants/price-list';
import { ADMIN_PERMISSION } from 'constants/permissions';
import { PRICE_REPORTS, PRICE_REPORTS_ROUTE, priceReportRoute } from 'constants/price-reports';

const Devices = lazy(() => import('./pages/devices'));
const UserSettings = lazy(() => import('./pages/user-settings'));
const DefaultSettings = lazy(() => import('./pages/default-settings'));
const ServiceMonitor = lazy(() => import('./pages/service-monitor'));
const CertRefresh = lazy(() => import('./pages/cert-refresh'));
const UploadMonitor = lazy(() => import('./pages/upload-monitor'));
const PriceUpload = lazy(() => import('./pages/price-upload'));
const DeletedDiamonds = lazy(() => import('./pages/deleted-diamonds'));
const DiamondSummary = lazy(() => import('./pages/diamond-summary'));

const GemstoneColumnMapping = lazy(() => import('./pages/gemstone-column-mapping'));
const GemstoneValueMapping = lazy(() => import('./pages/gemstone-value-mapping'));
const GemstoneManageValidValues = lazy(() => import('./pages/gemstone-manage-valid-values'));
const SourceVerification = lazy(() => import('./pages/source-verification'));
const SearchLogs = lazy(() => import('./pages/search-logs'));
const SearchLogsDefaultSettings = lazy(() => import('./pages/search-logs-default-settings'));
const SearchLogsUserSettings = lazy(() => import('./pages/search-logs-user-settings'));
const SearchLogsTierRules = lazy(() => import('./pages/search-logs-tier-rules'));
const SearchLogsTierOverrides = lazy(() => import('./pages/search-logs-tier-overrides'));
const SearchLogsTierTransitions = lazy(() => import('./pages/search-logs-tier-transitions'));
const SearchLogsAccountTiers = lazy(() => import('./pages/search-logs-account-tiers'));
const SearchLogsEventLog = lazy(() => import('./pages/search-logs-event-log'));
const ComplianceCountryRequirements = lazy(() => import('./pages/compliance-country-requirements'));
const ComplianceDocumentTypes = lazy(() => import('./pages/compliance-document-types'));
const RingOrders = lazy(() => import('./pages/ring-orders'));
const PriceListTexts = lazy(() => import('./pages/price-list-texts'));
const PriceListCurrencyRates = lazy(() => import('./pages/price-list-currency-rates'));
const PriceListPreview = lazy(() => import('./pages/price-list-preview'));
const PriceListTemplates = lazy(() => import('./pages/price-list-templates'));
const PriceListPublish = lazy(() => import('./pages/price-list-publish'));
const PriceListParcels = lazy(() => import('./pages/price-list-parcels'));
const PriceListRapi = lazy(() => import('./pages/price-list-rapi'));
const RapNetPriceLists = lazy(() => import('./pages/rapnet-price-lists'));
const PriceReport = lazy(() => import('./pages/price-report'));

// The price list screens are off in production until the new flow replaces the legacy one. They
// need the price list permission; RAPI only needs the user to sign in.
const priceListRoutes = CONFIG.PRICE_LIST_ENABLED
  ? [
      <PrivateRoute
        key="priceupload"
        path="/priceupload"
        component={PriceUpload}
        permission={PRICE_LIST_PERMISSION}
      />,
      <PrivateRoute
        key="texts"
        path="/pricelist/texts"
        component={PriceListTexts}
        permission={PRICE_LIST_PERMISSION}
      />,
      <PrivateRoute
        key="currencyrates"
        path="/pricelist/currencyrates"
        component={PriceListCurrencyRates}
        permission={PRICE_LIST_PERMISSION}
      />,
      <PrivateRoute
        key="preview"
        path="/pricelist/preview"
        component={PriceListPreview}
        permission={PRICE_LIST_PERMISSION}
      />,
      <PrivateRoute
        key="templates"
        path="/pricelist/templates"
        component={PriceListTemplates}
        permission={PRICE_LIST_PERMISSION}
      />,
      <PrivateRoute
        key="publish"
        path="/pricelist/publish"
        component={PriceListPublish}
        permission={PRICE_LIST_PERMISSION}
      />,
      <PrivateRoute
        key="parcels"
        path="/pricelist/parcels"
        component={PriceListParcels}
        permission={PRICE_LIST_PERMISSION}
      />,
      <PrivateRoute key="rapi" path="/pricelist/rapi" component={PriceListRapi} />
    ]
  : [];

export const Routes = () => {
  return (
    <Switch>
      <PrivateRoute path="/devices" component={Devices} />
      <PrivateRoute path="/defaultsettings" component={DefaultSettings} />
      <PrivateRoute path="/usresettings" component={UserSettings} />
      <PrivateRoute path="/servicemonitor" component={ServiceMonitor} />
      <PrivateRoute path="/uploadmonitor" component={UploadMonitor} />
      <PrivateRoute path="/certrefresh" component={CertRefresh} />
      <PrivateRoute path="/gemstonecolumnmapping" component={GemstoneColumnMapping} />
      <PrivateRoute path="/gemstonevaluemapping" component={GemstoneValueMapping} />
      <PrivateRoute path="/gemstonemanagevalidvalues" component={GemstoneManageValidValues} />
      <PrivateRoute path="/sourceverification" component={SourceVerification} />
      <PrivateRoute path="/searchlogs/defaultsettings" component={SearchLogsDefaultSettings} />
      <PrivateRoute path="/searchlogs/usersettings" component={SearchLogsUserSettings} />
      <PrivateRoute path="/searchlogs/tierrules" component={SearchLogsTierRules} />
      <PrivateRoute path="/searchlogs/tieroverrides" component={SearchLogsTierOverrides} />
      <PrivateRoute path="/searchlogs/tiertransitions" component={SearchLogsTierTransitions} />
      <PrivateRoute path="/searchlogs/accounttiers" component={SearchLogsAccountTiers} />
      <PrivateRoute path="/searchlogs/eventlog" component={SearchLogsEventLog} />
      <PrivateRoute path="/searchlogs" component={SearchLogs} />
      <PrivateRoute
        path="/compliancecountryrequirements"
        component={ComplianceCountryRequirements}
      />
      <PrivateRoute path="/compliancedocumenttypes" component={ComplianceDocumentTypes} />
      <PrivateRoute path="/ringorders" component={RingOrders} />
      {priceListRoutes}
      {/* The lists the legacy site publishes, so not behind PRICE_LIST_ENABLED. */}
      <PrivateRoute
        path="/pricelist/rapnetlists"
        component={RapNetPriceLists}
        permission={PRICE_LIST_PERMISSION}
      />
      {/* The Price Reports menu, a page per report; they only need the user to sign in, as RAPI does. */}
      <PrivateRoute path={`${PRICE_REPORTS_ROUTE}/:slug`} component={PriceReport} />
      <Route
        path={PRICE_REPORTS_ROUTE}
        exact
        render={() => <Redirect to={priceReportRoute(PRICE_REPORTS[0].slug)} />}
      />

      <PrivateRoute
        path="/deleteddiamonds"
        component={DeletedDiamonds}
        permission={ADMIN_PERMISSION}
      />
      <PrivateRoute
        path="/diamondsummary"
        component={DiamondSummary}
        permission={ADMIN_PERMISSION}
      />

      <Route path="/" exact render={() => <Redirect to="/devices" />} />
    </Switch>
  );
};

export default Routes;
